import React, { useMemo } from 'react';
import { Loader2, Palette } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCanvasStore, type Item } from '@/stores/canvasStore';
import type { ExtensionPanelProps } from '../registry';
import { ColorSwatches } from './ColorSwatches';
import { useImagePalette, usePaletteSettings, type PaletteSettings } from './hooks';

/** Enough to cover a moodboard without reading dozens of images at once. */
const MAX_IMAGES = 30;

/** Launcher panel: every image on the open board with its palette. */
export function PalettePanel({ open, onOpenChange }: ExtensionPanelProps) {
  const settings = usePaletteSettings();
  const items = useCanvasStore((s) => s.items);
  const images = useMemo(
    () => items.filter((i) => i.item_type === 'image' && (i.content as { url?: string }).url).slice(0, MAX_IMAGES),
    [items],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] p-0 gap-0 overflow-hidden rounded-2xl">
        <div className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4" style={{ color: '#8b5cf6' }} />
            Color Palette
          </DialogTitle>
          <DialogDescription className="mt-1 text-xs">
            Select any image on the canvas to see its colors underneath. Click a circle to copy the code.
          </DialogDescription>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 pb-5 space-y-2">
          {images.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No images on this board yet.</p>
          ) : (
            images.map((item) => <ImageRow key={item.id} item={item} settings={settings} active={open} />)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImageRow({ item, settings, active }: { item: Item; settings: PaletteSettings; active: boolean }) {
  const content = item.content as { url?: string; alt?: string };
  const swatches = useImagePalette(content.url, active);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/60 p-2">
      <img
        src={content.url}
        alt={content.alt || ''}
        className="h-12 w-12 rounded-lg object-cover shrink-0 bg-muted"
        loading="lazy"
        draggable={false}
      />
      {swatches === undefined ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : swatches === null ? (
        <span className="text-xs text-muted-foreground">Can't read colors from this image</span>
      ) : (
        <ColorSwatches swatches={swatches.slice(0, settings.colors)} format={settings.format} size={24} />
      )}
    </div>
  );
}
